import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Tooltip } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import { useGetBranches } from "hooks/useGetBranches";
import { useBranchesAntiRating } from "hooks/useTicketsAntiRating";
import { buildTicketsHref } from "helpers/ticketsHref";
import { AntiRatingTypeFilter, BranchesAntiRatingItem } from "interfaces/entities/ticketStats";

// Бэкенд возвращает только branchID + count. Координаты берём из списка филиалов.
type BranchRow = {
  id: string;
  name: string;
  code: string | null;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
};

type BranchOnMap = BranchRow & {
  count: number;
};

// Центр карты по умолчанию — Алматы. Используется только если у филиалов нет координат.
const DEFAULT_CENTER: [number, number] = [43.238949, 76.889709];
const DEFAULT_ZOOM = 5;

// Цвет маркера по количеству обращений — пустой/мало/средне/много.
function colorForCount(count: number): string {
  if (count === 0) return "#9CA3AF"; // gray
  if (count < 5) return "#10B981"; // green
  if (count < 20) return "#F59E0B"; // amber
  return "#EF4444"; // red
}

// Кастомная divIcon — кружок с цифрой количества обращений.
function makeIcon(count: number): L.DivIcon {
  const color = colorForCount(count);
  const size = count >= 100 ? 44 : count >= 10 ? 38 : 32;
  return L.divIcon({
    className: "tickets-map-marker",
    html: `<div style="
      width:${size}px;height:${size}px;border-radius:50%;
      background:${color};color:white;font-weight:700;font-size:13px;
      display:flex;align-items:center;justify-content:center;
      border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);
    ">${count}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
}

const TYPE_OPTIONS: { value: AntiRatingTypeFilter; label: string }[] = [
  { value: "ALL", label: "Все обращения" },
  { value: "COMPLAINT", label: "Жалобы" },
  { value: "SUGGESTION", label: "Предложения" },
  { value: "THANKS", label: "Благодарности" },
  { value: "OTHER", label: "Прочее" },
];

const TicketsMap = () => {
  const [type, setType] = useState<AntiRatingTypeFilter>("ALL");

  const orgID = localStorage.getItem("orgID") || "";

  const { data: branchesResponse, isLoading: branchesLoading } = useGetBranches({ orgID });

  // limit=0 — без обрезки топа, нужны все филиалы со счётчиками.
  const { data: antiRating, isLoading: countsLoading } = useBranchesAntiRating({ type, limit: 0 });

  const branchesOnMap = useMemo<BranchOnMap[]>(() => {
    const branches: BranchRow[] = (branchesResponse?.data as BranchRow[]) || [];
    const counts = new Map<string, number>(
      (antiRating?.items || []).map((i: BranchesAntiRatingItem) => [i.branchID || "", i.count]),
    );
    return branches
      .filter((b) => b.latitude != null && b.longitude != null)
      .map((b) => ({ ...b, count: counts.get(b.id) || 0 }));
  }, [branchesResponse, antiRating]);

  const branchesWithoutCoords = useMemo(() => {
    const branches: BranchRow[] = (branchesResponse?.data as BranchRow[]) || [];
    return branches.filter((b) => b.latitude == null || b.longitude == null);
  }, [branchesResponse]);

  // Центрируем карту по среднему между маркерами, иначе — дефолт.
  const center = useMemo<[number, number]>(() => {
    if (branchesOnMap.length === 0) return DEFAULT_CENTER;
    const sum = branchesOnMap.reduce(
      (acc, b) => ({ lat: acc.lat + (b.latitude || 0), lng: acc.lng + (b.longitude || 0) }),
      { lat: 0, lng: 0 },
    );
    return [sum.lat / branchesOnMap.length, sum.lng / branchesOnMap.length];
  }, [branchesOnMap]);

  const isLoading = branchesLoading || countsLoading;
  const totalCount = antiRating?.totalCount || 0;

  return (
    <div>
      <div
        className="flex-row flex-space-between"
        style={{ flexWrap: "wrap", alignItems: "center" }}
      >
        <h2 className="text-2xl m-b-16">Обращения на карте</h2>
        <div className="flex gap-4" style={{ alignItems: "center" }}>
          <Select
            size="small"
            value={type}
            onChange={(e) => setType(e.target.value as AntiRatingTypeFilter)}
          >
            {TYPE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="body2" color="text.secondary">
            Всего: <strong>{totalCount}</strong>
          </Typography>
        </div>
      </div>

      {branchesWithoutCoords.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {branchesWithoutCoords.length} филиал(ов) без координат не отображаются на карте.{" "}
          <Link to="/admin/branches">Заполнить координаты</Link>
        </Alert>
      )}

      {isLoading && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && branchesOnMap.length === 0 && (
        <Card>
          <CardContent>
            <Typography variant="body1">
              Нет филиалов с координатами для отображения на карте.{" "}
              <Link to="/admin/branches">Перейти к филиалам</Link>
            </Typography>
          </CardContent>
        </Card>
      )}

      {!isLoading && branchesOnMap.length > 0 && (
        <Box sx={{ height: "80vh", borderRadius: 1, overflow: "hidden" }}>
          <MapContainer
            center={center}
            zoom={DEFAULT_ZOOM}
            style={{ height: "100%", width: "100%" }}
            scrollWheelZoom
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {branchesOnMap.map((b) => (
              <Marker
                key={b.id}
                position={[b.latitude as number, b.longitude as number]}
                icon={makeIcon(b.count)}
              >
                <Tooltip direction="top" offset={[0, -16]} opacity={0.95}>
                  <strong>{b.name}</strong> — {b.count} обращ.
                </Tooltip>
                <Popup>
                  <div style={{ minWidth: 180 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{b.name}</div>
                    {b.code && (
                      <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 4 }}>
                        Код: {b.code}
                      </div>
                    )}
                    {b.address && <div style={{ fontSize: 12, marginBottom: 8 }}>{b.address}</div>}
                    <div style={{ marginBottom: 8 }}>
                      Обращений: <strong>{b.count}</strong>
                    </div>
                    <Button
                      size="small"
                      variant="contained"
                      component={Link}
                      style={{ color: "white" }}
                      to={buildTicketsHref({
                        branchID: b.id,
                        type: type === "ALL" ? undefined : type,
                      })}
                    >
                      Открыть обращения
                    </Button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </Box>
      )}
    </div>
  );
};

export default TicketsMap;
